import { useEffect, useState } from 'react';
import { useCopiedDoc } from '../../../../hooks/useCopiedDoc';
import { copyDocToClipboard } from '../../../../utils/docClipboard';
import { pasteCopiedDoc } from '../../../../utils/pasteDoc';
import { DOC_DRAG, FOLDER_DRAG } from '../utils/browseUtils';

export function useBrowseActions({
  organization,
  folderId,
  onCreateDoc,
  createDoc,
  onNavigateFolder,
  onOpenDoc,
  onOpenDocInNewTab,
}) {
  const [dropTargetId, setDropTargetId] = useState(null);
  const [menu, setMenu] = useState(null);
  const [pasting, setPasting] = useState(false);
  const copied = useCopiedDoc();

  const handleCreateFolder = () => {
    const name = window.prompt('Folder name');
    if (!name?.trim()) return;
    organization?.createFolder?.(name.trim(), folderId || null);
  };

  const handleCreateDoc = async () => {
    const doc = await onCreateDoc?.();
    if (doc?.id && folderId) {
      organization?.moveDocToFolder?.(doc.id, folderId);
    }
  };

  const openMenu = (e, next) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, ...next });
  };

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const timer = window.setTimeout(() => {
      document.addEventListener('click', close);
      document.addEventListener('contextmenu', close);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close);
    };
  }, [menu]);

  const handleCopyDoc = (item) => {
    copyDocToClipboard(item.doc || { id: item.id, title: item.name });
  };

  const handlePasteDoc = async (targetFolderId) => {
    if (!copied?.docId || !createDoc || pasting) return;
    setPasting(true);
    try {
      await pasteCopiedDoc({
        createDoc,
        moveDocToFolder: organization?.moveDocToFolder,
        folderId: targetFolderId || null,
      });
    } finally {
      setPasting(false);
    }
  };

  const onDragOver = (e, targetId) => {
    if (
      !e.dataTransfer.types.includes(DOC_DRAG) &&
      !e.dataTransfer.types.includes(FOLDER_DRAG)
    ) {
      return;
    }
    e.preventDefault();
    setDropTargetId(targetId);
  };

  const onDropFolder = (e, targetFolderId) => {
    e.preventDefault();
    setDropTargetId(null);
    const docId = e.dataTransfer.getData(DOC_DRAG);
    const movedFolder = e.dataTransfer.getData(FOLDER_DRAG);
    if (docId) organization?.moveDocToFolder?.(docId, targetFolderId);
    else if (movedFolder && movedFolder !== targetFolderId) {
      organization?.moveFolder?.(movedFolder, targetFolderId);
    }
  };

  const openItem = (item) => {
    if (item.kind === 'folder') onNavigateFolder?.(item.id);
    else onOpenDoc?.(item.id);
  };

  const openItemInNewTab = (item) => {
    if (item.kind === 'folder' || !item?.id) return;
    onOpenDocInNewTab?.(item.id);
  };

  return {
    dropTargetId,
    setDropTargetId,
    menu,
    setMenu,
    pasting,
    copied,
    handleCreateFolder,
    handleCreateDoc,
    handleCopyDoc,
    handlePasteDoc,
    onDragOver,
    onDropFolder,
    openItem,
    openItemInNewTab,
    openMenu,
  };
}
